<template>
  <section>
    <v-dialog v-model="visible" max-width="700px" @keydown.esc="close">

      <v-card>
        <v-card-title>
          <span v-if="isTimeEntryCreation" class="headline">
            Zeiteintrag erstellen
          </span>

          <span v-else class="headline">
            Zeiteintrag aktualisieren {{ this.editedActivity.isEditable == false ? "(Aufgabe gesperrt)" : "" }}
          </span>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="4">
                <v-text-field placeholder="DD.MM.YYYY" 
                  label="Datum"
                  :value="editedPresence.date.textfield"
                  dense
                  @keydown="onDateFieldChange($event)"
                  @input="updatePresenceDateTextfieldValue($event), focusElementIf($event.replace(/[^0-9]/g, '').length == 8, $refs['setFrom'])"
                  @focus="$event.target.select()"
                  @keydown.enter="save"
                  @blur="onDateSet($event)">
                  <template v-slot:append>
                    <v-menu ref="editedPresence.date.picker" v-model="editedPresence.date.picker" :close-on-content-click="false"
                      transition="slide-y-transition">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on" class="mt-1 grow-icon" size="20" tabindex="-1">mdi-calendar</v-icon>
                      </template>
                      <v-date-picker color="#c00000" no-title scrollable v-model="editedPresence.date.value"
                        @input="updatePresenceDateValue($event), focusElementIf($event.replace(/[^0-9]/g, '').length == 8, $refs['setFrom'])"/>
                    </v-menu>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="4">
                <v-text-field
                  data-cy="cepa-from"
                  ref="setFrom"
                  v-model="editedPresence.from"
                  label="Von"
                  placeholder=" "
                  dense
                  :maxlength="5"
                  :disabled="presenceEditAllowed == false"
                  @keydown="onTimeFieldChange($event)"
                  @input="(value) => focusElementIf(value.replace(/[^0-9]/g, '').length == 4, $refs['setTo'])"
                  @blur="onFromTimeSet($event)"
                  @focus="$event.target.select()"
                  @keydown.enter="save"
                />
              </v-col>
              <v-col cols="4">
                <v-text-field
                  data-cy="cepa-to"
                  ref="setTo"
                  v-model="editedPresence.to"
                  label="Bis"
                  placeholder=" "
                  dense
                  :maxlength="5"
                  :disabled="presenceEditAllowed == false"
                  @keydown="onTimeFieldChange($event)"
                  @input="(value) => focusElementIf(value.replace(/[^0-9]/g, '').length == 4, $refs['selectProject'].$refs.input)"
                  @blur="onToTimeSet($event)"
                  @focus="$event.target.select()"
                  @keydown.enter="save"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <v-autocomplete
                  data-cy="cepa-project"
                  ref="selectProject"
                  v-model="editedActivity.project.id"
                  :items="select.projects"
                  :disabled="activityEditAllowed == false"
                  :item-text="(item) => projectDisplayName(item)"
                  item-value="id"
                  label="Projekt"
                  dense
                  @change="(value) => onSelectProject(value, $refs['selectTask'].$refs.input)"
                ></v-autocomplete>
              </v-col>
              <v-col cols="6">
                <v-autocomplete
                  data-cy="cepa-task"
                  ref="selectTask"
                  v-model="editedActivity.task.id"
                  :items="select.tasks"
                  :disabled="activityEditAllowed == false"
                  item-text="name"
                  item-value="id"
                  @change="() => onSelectTask($refs['description'])"
                  label="Aufgabe"
                  dense
                ></v-autocomplete>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  data-cy="cepa-description"
                  ref="description"
                  v-model="editedActivity.description"
                  :disabled="activityEditAllowed == false"
                  label="Beschreibung"
                  autofocus
                  dense
                  @input="extractFromTo($event)"
                  @keydown.enter="save"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-btn v-if="!isTimeEntryCreation" color="#c00000" class="white--text" @click="deleteEntry()" :disabled="saving || (activityEditAllowed == false && presenceEditAllowed == false)" data-cy="cepa-delete">
            Löschen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="#c00000" class="white--text" @click="close()" :disabled="saving" data-cy="cepa-cancel">
            Abbrechen
          </v-btn>
          <v-btn color="#c00000" class="white--text" @click="save()" :disabled="saving || (activityEditAllowed == false && presenceEditAllowed == false)" data-cy="cepa-save">
            {{ isTimeEntryCreation ? 'Erstellen' : 'Aktualisieren' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    
    </v-dialog>

    <Confirm ref="confError" confirmButtonText="OK" cancelButtonText=""></Confirm>
  </section>
</template>

<script>
  import * as mocoClient from '@client/client.moco';
  import Confirm from '@components/Confirm.vue';
  import * as monatsabschlussClient from '@client/client.monatsabschluss';
  import dayjs from '@util/dayjs';

  export default {
    name: 'CreateEditTimeEntry',
    components: {
      Confirm
    },
    props: ['dayData', 'alterOriginalEntries'],
    mounted() {
      this.select.projects = this.arraySortByName([...this.$store.getters.staticUserData.bookableProjects]);
    },

    data() {
      return {
        visible: false,
        presenceEditAllowed: true,
        activityEditAllowed: true,
        saving: false,
        initial: {
          activity: undefined,
          projectID: undefined,
          taskID: undefined
        },
        editedPresence: { 
          id: undefined,
          from: undefined,
          to: undefined,
          date: {
            textfield: undefined,
            value: null,
            picker: false
          }
        },
        editedActivity: {
          id: undefined,
          description: undefined,
          isEditable: undefined,
          project: {
            id: undefined
          },
          task: {
            id:undefined
          },
        },
        select: {
          projects: [],
          tasks: [],
        },
      }
    },

    computed: {
      descriptionIncludesTimeSpan() {
        const workingTimeRE = /^(HO )?\(?([012]?\d:?\d\d) ?- ?([012]?\d:?\d\d)\)?(.*)/gm; //^\(?([012]?\d:?\d\d)-([012]?\d:?\d\d)\)?(.*)
        return workingTimeRE.exec(this.editedActivity.description) !== null;
      },

      isTimeEntryCreation() {
        return !this.editedActivity.id && !this.editedPresence.id;
      }
    },

    methods: {
      onTimeFieldChange(event) {
        if (
          /[0-9]/.test(event.key) ||
          event.key === ':' ||
          event.key === 'Backspace' ||
          event.key === 'Tab' ||
          event.metaKey === true
        ) {
          return;
        } else {
          event.preventDefault();
        }
      },

      onDateFieldChange(event) {
        if (
          /[0-9]/.test(event.key) ||
          event.key === '.' ||
          event.key === '/' ||
          event.key === '-' ||
          event.key === 'Backspace' ||
          event.key === 'Tab' ||
          event.metaKey === true
        ) {
          return;
        } else {
          event.preventDefault();
        }
      },

      onFromTimeSet() {
        this.editedPresence.from = this.formatTime(this.editedPresence.from);
      },

      onToTimeSet() {
        this.editedPresence.to = this.formatTime(this.editedPresence.to);
      },

      onDateSet() {
        this.editedPresence.date.textfield = this.formatDate(this.editedPresence.date.textfield);
        if (dayjs(this.editedPresence.date.textfield, 'DD.MM.YYYY').isValid()) {
          this.editedPresence.date.value = dayjs(this.editedPresence.date.textfield, 'DD.MM.YYYY').format('YYYY-MM-DD');
        } else {
          this.editedPresence.date.value = null;
        }
      },

      updatePresenceDateTextfieldValue(value) {
        this.editedPresence.date.textfield = value;
      },

      updatePresenceDateValue(value) {
        this.editedPresence.date.value = value;
        this.editedPresence.date.textfield = dayjs(value, 'YYYY-MM-DD').format('DD.MM.YYYY');
        this.editedPresence.date.picker = false;
      },

      onSelectProject(projectID, element) {
        this.setTasksForProject(projectID);

        if (element) {
          element.focus();
        }
      },

      onSelectTask(element) {
        if (element) {
          element.focus();
        }
      },

      formatTime(time) {
        let convertedValue;
        if (/^[0-9][0-9]:[0-9][0-9]$/.test(time)) {
          convertedValue = time
        } else if (/^[0-9]:[0-9][0-9]$/.test(time)) {
          convertedValue = `0${time}`;
        } else if (/^[0-9]{3}$/.test(time)) {
          const helperValue = `0${time}`;
          convertedValue = `${helperValue.substring(0, 2)}:${helperValue.substring(2)}`;
        } else if (/^[0-9]{4}$/.test(time)) {
          convertedValue = `${time.substring(0, 2)}:${time.substring(2)}`;
        }

        if (!dayjs(convertedValue, 'HH:mm', true).isValid()) {
          return '??:??';
        } else {
          return convertedValue;
        }
      },

      formatDate(date) {
        const trimedDate = date.toString().trim();
        let convertedValue;
        // T.M.JJJJ, T-M-JJJJ, T/M/JJJJ, wobei T und M 1- oder 2-stellig sein können
        if (/^\d{1,2}[.\-/]\d{1,2}[.\-/]\d{4}$/.test(trimedDate)) { 
          const parts = trimedDate.split(/[.\-/]/); 
          convertedValue = `${parts[0].padStart(2, '0')}.${parts[1].padStart(2, '0')}.${parts[2]}`; 
        } 
        //TTMMJJJJ
        else if(/^\d{8}$/.test(trimedDate)) { 
          convertedValue = `${trimedDate.substring(0, 2)}.${trimedDate.substring(2, 4)}.${trimedDate.substring(4)}`;
        } 
        // TMMJJJJ oder TTMJJJJ
        else if(/^\d{7}$/.test(trimedDate)) {
          const date1 = `${trimedDate.substring(0, 2)}.0${trimedDate.substring(2, 3)}.${trimedDate.substring(3)}`;
          const date2 = `0${trimedDate.substring(0, 1)}.${trimedDate.substring(1, 3)}.${trimedDate.substring(3)}`;
          convertedValue = this.getCloserDate(date1, date2);
        } 
        // TMJJJJ
        else if(/^\d{6}$/.test(trimedDate)) {
          convertedValue = `0${trimedDate.substring(0, 1)}.0${trimedDate.substring(1, 2)}.${trimedDate.substring(2)}`;
        }

        if (!dayjs(convertedValue, 'DD.MM.YYYY', true).isValid()) {
          return '??.??.????';
        } else {
          return convertedValue;
        }
      },

      getCloserDate(date1, date2) {
        const parsedDate1 = dayjs(date1, 'DD.MM.YYYY', true);
        const parsedDate2 = dayjs(date2, 'DD.MM.YYYY', true);
        
        if (parsedDate1.isValid() && parsedDate2.isValid()) {
          const now = dayjs();
          const diff1 = Math.abs(now.diff(parsedDate1));
          const diff2 = Math.abs(now.diff(parsedDate2));

          return diff1 < diff2 ? date1 : date2;
        }

        if (parsedDate1.isValid()){
          return date1;
        }
        if (!parsedDate2.isValid()){
          return date2;
        }
        return '??.??.????';
      },
      
      focusElementIf(shouldFocus, element) {
        if (shouldFocus) {
          element.focus();
        }
      },

      extractFromTo(description){
        const workingTimeRE = /^(HO )?\(?([012]?\d:?\d\d) ?- ?([012]?\d:?\d\d)\)?(.*)/gm; //^\(?([012]?\d:?\d\d)-([012]?\d:?\d\d)\)?(.*)
        const match = workingTimeRE.exec(description);
        if (match !== null) {
          this.presenceEditAllowed = false;
          this.editedPresence.from = this.formatTime(match[2]);
          this.editedPresence.to = this.formatTime(match[3]);
        } else {
          this.presenceEditAllowed = true;
        }
        return;
      },

      createTimeEntry(preSelectedProject, preselectedTask) {
        this.presenceEditAllowed = true;
        this.activityEditAllowed = true;
        this.visible = true;

        const today = dayjs();
        this.editedPresence.date.value = today.format('YYYY-MM-DD');
        this.editedPresence.date.textfield = today.format('DD.MM.YYYY');

        if (preSelectedProject && preselectedTask) {
          this.setTasksForProject(preSelectedProject.id);
          this.editedActivity.description = '';
          this.editedActivity.isEditable = true;
          this.editedActivity.project.id = preSelectedProject.id;
          this.editedActivity.task.id = preselectedTask.id;
        }
      },

      editPresence(presence) {
        this.setPresence(presence);

        if (presence.activityId) {
          const matchedActivity = this.dayData.activities.find((activity) => {
            return activity.id == presence.activityId;
          });

          if (matchedActivity) {
            this.setActivity(matchedActivity);
          }
        }

        this.presenceEditAllowed = this.editedPresence.id != undefined && this.editedActivity.isEditable;
        this.activityEditAllowed = this.editedActivity.id != undefined && this.editedActivity.isEditable;
        this.visible = true;
      },

      editActivity(activity, prohibitPresenceEdit) {
        this.setActivity(activity);

        if (activity.presenceId && this.dayData.presences) {
          const matchedPresence = this.dayData.presences.find((presence) => {
            return presence.id == activity.presenceId;
          });

          if (matchedPresence) {
            this.setPresence(matchedPresence);
          }
        }

        if (this.editedPresence.id == undefined) {
          const emptyPresence = {
            from: '-',
            to: '-',
            date: {
              value: '-',
              textfield: '-',
              picker: false
            }
          };

          this.setPresence(emptyPresence);
        }

        this.presenceEditAllowed = this.editedPresence.id != undefined && !prohibitPresenceEdit && this.editedActivity.isEditable;
        this.activityEditAllowed = this.editedActivity.id != undefined && this.editedActivity.isEditable;
        this.visible = true;
      },

      setTasksForProject(projectID) {
        const selectedProject = this.select.projects.find((project) => project.id == projectID);

        if (selectedProject) {
          // TODO wenn das neue projekt den alten task auch hat, dann den selektieren! bsp: wechsel von razzfaz.io auf yorik
          const filteredTasks = this.editedActivity?.isEditable ? [...selectedProject.tasks].filter((task) => task.active == true || task.active == undefined) : [...selectedProject.tasks];
          this.select.tasks = this.arraySortByName(filteredTasks);
          this.editedActivity.task.id = selectedProject.tasks[0].id;
        } else {
          this.select.tasks = [];
          this.editedActivity.project.id = undefined;
        }
      },

      async save() {
        // TODO - Aufbau diskutieren. Ähnlch wie bei loadMonth muss etwas "davor" und "danach" gemacht werden
        // TODO entweder direkt in der Methode darum kümmern oder eine "helper" drumherum machen

        // Zeitformat update (falls das noch nicht passiert ist)
        this.onFromTimeSet();
        this.onToTimeSet();
        this.onDateSet();
        
        if (this.editedPresence.date.textfield === '??.??.????'){
          await this.$refs.confError.open('Update Error', 'Die Presence kann nicht gespeichert werden.', 'Das Datum ist im falschen Format.');
          return;
        }

        if (this.editedPresence.from === '??:??'){
          await this.$refs.confError.open('Update Error', 'Die Presence kann nicht gespeichert werden.', 'Die "Von" Uhrzeit ist im falschen Format.');
          return;
        }

        if (this.editedPresence.to === '??:??'){
          await this.$refs.confError.open('Update Error', 'Die Presence kann nicht gespeichert werden.', 'Die "Bis" Uhrzeit ist im falschen Format.');
          return;
        }

        const staticUserData = this.$store.getters.staticUserData;
        if (staticUserData == undefined || this.$store.getters.userAuthenticationData.mocoApiKey == undefined) {
          return;
        }

        const jiraTicket = await monatsabschlussClient.getMonatsabschluss(undefined, staticUserData.user.email, dayjs(this.editedPresence.date.value).isoMonth(), dayjs(this.editedPresence.date.value).year());
        if (jiraTicket && jiraTicket.status != global.monthClosing.status.open && jiraTicket.status != global.monthClosing.status.rejected) {
          await this.$refs.confError.open('Update Error', 'Die Presence kann nicht gespeichert werden.', 'Die Presence darf nicht in ein bereits abgeschlossenes Monat verschoben werden.');
          return;
        }

        const employmentStart = this.$store.getters.staticUserData.user.activeEmployment.from; 
        const employmentEnd = this.$store.getters.staticUserData.user.activeEmployment.to; 
        if (dayjs(this.editedPresence.date.value).isBefore(employmentStart) || dayjs(this.editedPresence.date.value).isAfter(employmentEnd)){
          await this.$refs.confError.open('Update Error', 'Die Presence kann nicht gespeichert werden.', 'Die Presence kann nicht in einen Monat verschoben werden, in dem der Nutzer nicht angestellt ist.');
          return;
        }

        this.saving = true;

        const updateError = {};
        
        if (this.isTimeEntryCreation) {
          if (this.descriptionIncludesTimeSpan) {
            try {
              await mocoClient.createActivity(dayjs(this.editedPresence.date.value), this.editedActivity.description, this.editedActivity.project.id, this.editedActivity.task.id);
            } catch (e) {
              updateError.message = 'Die Activity konnte nicht erstellt werden.';
              updateError.details = 'Moco Error: ' + Object.entries(e.response?.data)?.map((entry) => entry[1]).join(', ');
            }
          } else {
            try {
              const createdPresence = await mocoClient.createPresence(dayjs(this.editedPresence.date.value), this.editedPresence.from, this.editedPresence.to, false);
              const presenceDuration = this.calcTimeDifferenceAsHours(createdPresence.from, createdPresence.to);
              
              try {
                await mocoClient.createActivity(dayjs(this.editedPresence.date.value), this.editedActivity.description, this.editedActivity.project.id, this.editedActivity.task.id, presenceDuration, String(createdPresence.id));
              } catch (e) {
                updateError.message = 'Die Activity konnte nicht erstellt werden.';
                updateError.details = 'Moco Error: ' + Object.entries(e.response?.data)?.map((entry) => entry[1]).join(', ');
              }
            } catch (e) {
              updateError.message = 'Die Presence konnte nicht erstellt werden.';
              updateError.details = 'Moco Error: ' + Object.entries(e.response?.data)?.map((entry) => entry[1]).join(', ');
            }
          }
        } else {
          let updatedPresence;
          if (this.editedPresence.id) {
            try {
              // Sequentiell, damit das Activity Update erst gar nicht gemacht wird, wenn schon das Presence Update fehl schlägt
              updatedPresence = await mocoClient.updatePresence(this.editedPresence.id, dayjs(this.editedPresence.date.value), this.editedPresence.from, this.editedPresence.to); 
            } catch (e) {
              updateError.message = 'Die Presence konnte nicht gespeichert werden.';
              updateError.details = 'Moco Error: ' + Object.entries(e.response?.data)?.map((entry) => entry[1]).join(', ');
            }
          }

          if (this.editedActivity.id && updateError.message == undefined) {
            const presenceDuration = updatedPresence ? this.calcTimeDifferenceAsHours(updatedPresence.from, updatedPresence.to) : undefined;
            try {
              await mocoClient.updateActivity(this.editedActivity.id, this.editedActivity.project.id, this.editedActivity.task.id, dayjs(this.editedPresence.date.value),
                this.editedActivity.description, presenceDuration);

              if (this.initial.projectID !== this.editedActivity.project.id || this.initial.taskID !== this.editedActivity.task.id) {
                this.$root.$emit('activityTreeReloadNeeded');
              } else if (this.alterOriginalEntries) {
                this.initial.activity.description = this.editedActivity.description;
                this.initial.activity.highlightedDescription = this.editedActivity.description;
              }
            } catch (e) {
              if (this.editedPresence.id) {
                const originalPresence = this.dayData.presences.find((presence) => {
                  return presence.id == this.editedPresence.id;
                });

                try {
                  updatedPresence = await mocoClient.updatePresence(originalPresence.id, originalPresence.from, originalPresence.to);
                  updateError.message = 'Die Activity konnte nicht gespeichert werden.' + (updatedPresence ? ' Die Presence wurde wieder zurückgesetzt.' : '');
                } catch (e) {
                  updateError.message = 'Die Activity konnte nicht gespeichert und es gab ein Problem beim Rücksetzen der zugehörigen Presence. Irgendwas ist jetzt wahrscheinlich kaputt :)';
                  updateError.details = 'Moco Error: ' + Object.entries(e.response?.data)?.map((entry) => entry[1]).join(', ');
                }
              }
            }
          }
        }
        
        if (updateError.message) {
          await this.$refs.confError.open('Update Error', updateError.message, updateError.details);
          this.saving = false;
          return;
        }

        this.close(true);
      },

      async deleteEntry() {
        this.saving = true;
        
        const deleteError = {};
        if (this.editedPresence.id) {
          try {
            // Sequentiell, damit die Activity erst gar nicht gelöscht wird, wenn schon die Presence nicht gelöscht werden konnte
            await mocoClient.deletePresence(this.editedPresence.id);
          } catch (e) {
            deleteError.message = 'Die Presence konnte nicht gelöscht werden!';
            deleteError.details = 'Moco Error: ' + Object.entries(e.response?.data)?.map((entry) => entry[1]).join(', ');
          }
        }

        if (this.editedActivity.id && deleteError.message == undefined) {
          try {
            await mocoClient.deleteActivity(this.editedActivity.id);
          } catch (e) {
            deleteError.message = 'Die Activity konnte nicht gelöscht werden!';
            deleteError.details = 'Moco Error: ' + Object.entries(e.response?.data)?.map((entry) => entry[1]).join(', ');
          }
        }

        if (deleteError.message) {
          await this.$refs.confError.open('Delete Error', deleteError.message, deleteError.details);
        }

        this.close(true);
      },

      close(reloadApp) {
        this.saving = false;
        if (reloadApp) {
          this.$root.$emit('reloadApp', this);
        }

        this.visible = false;

        this.setPresence();
        this.setActivity();
        this.select.tasks = [];
      },

      setPresence(presence) {
        this.editedPresence.id = presence?.id;
        this.editedPresence.from = presence?.from;
        this.editedPresence.to = presence?.to;
        this.editedPresence.date.textfield = dayjs(presence?.date, 'YYYY-MM-DD').format('DD.MM.YYYY') ?? undefined;
        this.editedPresence.date.value = presence?.date.value ?? null;
        this.editedPresence.date.picker = presence?.date.picker ?? false;
      },

      setActivity(activity) {
        this.editedActivity.id = activity?.id;
        this.editedActivity.description = activity?.description;
        this.editedActivity.isEditable = activity?.isEditable;
        this.editedActivity.project.id = activity?.project.id;
        this.editedActivity.task.id = activity?.task.id;

        this.initial.projectID = activity?.project?.id;
        this.initial.taskID = activity?.task?.id;
        this.initial.activity = activity;

        this.setTasksForProject(activity?.project?.id);
        this.editedActivity.task.id = activity?.task?.id;
      }
    }
  };
</script>